import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api } from "@/lib/apiClient";
import { Wind, Sparkles, Layers, Footprints, MessageSquare, PenLine } from "lucide-react";
import { toast } from "sonner";

const TOOL = {
  reset: { label: "Reset", icon: Wind },
  immersion: { label: "Immersion", icon: Sparkles },
  installation: { label: "Installation", icon: Layers },
  mission: { label: "Mission", icon: Footprints },
  practice: { label: "Practice", icon: MessageSquare },
};

const dayLabel = (iso) => {
  const d = new Date(iso); const today = new Date();
  const diff = Math.round((new Date(today.toDateString()) - new Date(d.toDateString())) / 86400000);
  if (diff === 0) return "Today";
  if (diff === 1) return "Yesterday";
  return d.toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "short" });
};

// A gentle timeline of what he's done with his Coach.
function NoteEditor({ e, onSaved }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState(e.note || "");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { await api.put(`/experiences/${e.id}/note`, { note: text }); onSaved(text); setOpen(false); toast.success("Saved — your Coach will remember this."); }
    catch { toast.error("Couldn't save that note."); } finally { setBusy(false); }
  };
  if (!e.id) return null;
  if (!open) return (
    <button onClick={() => setOpen(true)} className="text-[11px] text-muted-foreground hover:text-primary flex items-center gap-1 mt-1 transition-colors" data-testid={`journal-note-btn-${e.id}`}>
      <PenLine className="w-3 h-3" /> {e.note ? "Edit note" : "Add a note"}
    </button>
  );
  return (
    <div className="mt-2 flex gap-2 items-start">
      <input autoFocus value={text} maxLength={300} onChange={(ev) => setText(ev.target.value)} onKeyDown={(ev) => ev.key === "Enter" && save()}
        placeholder="What stayed with you?" className="flex-1 min-w-0 rounded-lg bg-white/[0.04] border border-white/10 px-3 py-1.5 text-sm outline-none focus:border-primary/50" data-testid={`journal-note-input-${e.id}`} />
      <button onClick={save} disabled={busy} className="text-xs rounded-full bg-primary text-primary-foreground px-3 py-1.5 disabled:opacity-50" data-testid={`journal-note-save-${e.id}`}>Save</button>
    </div>
  );
}

export default function ExperienceJournal() {
  const [items, setItems] = useState(null);
  const setNote = (id, note) => setItems((xs) => xs.map((x) => (x.id === id ? { ...x, note } : x)));
  useEffect(() => { api.get("/experiences").then((r) => setItems(r.data.items)).catch(() => setItems([])); }, []);
  if (!items) return null;
  const groups = items.reduce((acc, e) => { const k = dayLabel(e.at); (acc[k] = acc[k] || []).push(e); return acc; }, {});
  return (
    <section className="mb-10" data-testid="experience-journal">
      <p className="text-xs uppercase tracking-[0.22em] text-primary mb-1">Your journal</p>
      <h2 className="font-serif text-2xl sm:text-3xl mb-6">What we've done together</h2>
      {!items.length && <p className="text-sm text-muted-foreground" data-testid="journal-empty">Your Resets, Immersions and Missions will gather here — quietly, as you go.</p>}
      <div className="relative border-l border-white/10 ml-2 space-y-8">
        {Object.entries(groups).map(([day, list], gi) => (
          <motion.div key={day} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: gi * 0.06 }} className="pl-6">
            <p className="text-xs text-muted-foreground mb-3 -ml-[1.85rem] flex items-center gap-3"><span className="w-2.5 h-2.5 rounded-full bg-primary/60 ring-4 ring-background" />{day}</p>
            <div className="space-y-3">
              {list.map((e, i) => {
                const t = TOOL[e.tool] || TOOL.reset; const Icon = t.icon;
                return (
                  <div key={`${e.at}-${i}`} className="flex gap-3" data-testid={`journal-item-${e.tool}`}>
                    <Icon className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm"><span className="text-muted-foreground">{t.label} · </span>{e.title || t.label}</p>
                      {e.note && <p className="text-xs text-muted-foreground italic mt-0.5 line-clamp-2" data-testid="journal-note-text">“{e.note}”</p>}
                      <NoteEditor e={e} onSaved={(n) => setNote(e.id, n)} />
                    </div>
                  </div>
                );
              })}
            </div>
          </motion.div>
        ))}
      </div>
    </section>
  );
}
